import { useState } from 'react';

import * as Sentry from '@sentry/react';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { AGGREGATE_NAMES, HTTP_STATUS, LOAN_REQUEST_COMMANDS, UPLOAD_IDLE_TIMEOUT_MS } from 'cpool-constants';
import { schemaDocumentsUploadResponse } from 'cpool-schemas';
import { useTranslation } from 'react-i18next';
import { LuDownload, LuUpload } from 'react-icons/lu';
import { toast } from 'react-toastify';

import { useRefreshRecordedDocuments } from '../Table/useRefreshRecordedDocuments';
import { APICommunicator } from '~/api/APICommunicator';
import UploadModal from '~/components/UploadModal';
import type { RemoveUploadedHandler, UploadFileHandler } from '~/components/UploadModal/types';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import useLead from '~/features/leadDetails/hooks/useLead';
import { downloadFile } from '~/utils/downloadFile';
import uploadFiles from '~/utils/uploadFiles';

type Props = {
    leadId: string;
};

const DocumentActions: React.FC<Props> = ({ leadId }) => {
    const { t } = useTranslation();
    const [showUploadModal, setShowUploadModal] = useState(false);
    const { data: lead } = useLead(leadId);
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const refreshRecordedDocuments = useRefreshRecordedDocuments(leadId);

    const isDisabledDocumentDownload = !permissions.api.documentDownload;
    const isDisabledDocumentUpload = !permissions.api.documentUpload;

    if (!lead) return null;

    const { id, ...data } = lead;

    let filename = 'dokumente.zip';
    if (data.antragsteller1.personendaten)
        filename = `${data.antragsteller1.personendaten.vorname}_${data.antragsteller1.personendaten.nachname}_dokumente.zip`;

    const download = async () => {
        try {
            await downloadFile({
                mimeType: 'application/zip',
                name: filename,
                route: `/documents/download?aggregateId=${id}`,
            });
        } catch (error) {
            if (error instanceof Error && error.message.includes('No files found')) {
                toast.warn(t('FILE_MANAGER.NO_FILES'));
                return;
            }
            Sentry.captureException(error);
            toast.error(t('OFFER_MANAGEMENT.TOASTS.DOWNLOAD_FAILED'));
        }
    };

    const uploadFile: UploadFileHandler = async (file, { onProgress, signal }) => {
        const formData = new FormData();
        formData.append('0', file);
        const response = await uploadFiles(
            `/documents/upload?aggregateId=${id}`,
            { body: formData, credentials: 'include', method: 'post' },
            { idleTimeout: UPLOAD_IDLE_TIMEOUT_MS, onProgress, signal },
        );

        // A cancel after the response arrived still needs the key for the rollback
        if (response.status === 0 && signal.aborted) throw new DOMException('Aborted', 'AbortError');

        if (response.status === 0) throw new Error(t('FILE_UPLOAD.NETWORK_ERROR'));
        if (response.status === HTTP_STATUS.REQUEST_TIMEOUT) throw new Error(t('FILE_UPLOAD.TIMEOUT'));
        if (response.status >= HTTP_STATUS.SERVER_ERROR) throw new Error(t('FILE_UPLOAD.SERVER_ERROR'));
        if (response.status === HTTP_STATUS.UNAUTHORIZED || response.status === HTTP_STATUS.FORBIDDEN)
            throw new Error(t('FILE_UPLOAD.NOT_PERMITTED'));

        const parsed = schemaDocumentsUploadResponse.safeParse(response.body);
        if (parsed.success && parsed.data.malwareRejectedFiles.length > 0)
            throw new Error(t('FILE_UPLOAD.MALWARE_REJECTED', { files: parsed.data.malwareRejectedFiles.join(', ') }));
        if (response.status !== HTTP_STATUS.OK) {
            // Server texts are English/technical; keep them for Sentry via cause, show the translated message
            const serverMessage = (response.body as { message?: string } | null)?.message;
            const error = new Error(t('FILE_UPLOAD.UPLOAD_FAILED'));
            throw serverMessage ? Object.assign(error, { cause: new Error(serverMessage) }) : error;
        }
        if (!parsed.success) throw new Error(t('FILE_UPLOAD.UPLOAD_FAILED'));

        const { errorUploadFiles, position, rejectedFiles, uploadedDocuments } = parsed.data;
        // A server-side type/size rejection or a per-file upload error is surfaced as the neutral generic message.
        if (rejectedFiles.length > 0 || errorUploadFiles.length > 0) throw new Error(t('FILE_UPLOAD.UPLOAD_FAILED'));
        const [uploaded] = uploadedDocuments ?? [];
        if (!uploaded) throw new Error(t('FILE_UPLOAD.UPLOAD_FAILED'));

        // Cancelled after the server stored it; the queue rolls it back
        if (signal.aborted) return uploaded;

        await refreshRecordedDocuments(position);
        return uploaded;
    };

    const removeUploaded: RemoveUploadedHandler = async ({ key }) => {
        const { position } = await APICommunicator.sendCommand({
            aggregateId: id,
            aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
            commandName: LOAN_REQUEST_COMMANDS.removeDocument,
            payload: { key },
        });
        await refreshRecordedDocuments(position);
    };

    return (
        <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
                variant={ButtonVariant.SECONDARY}
                iconLeft={<LuDownload size={14} />}
                disabled={isDisabledDocumentDownload}
                onClick={download}
            >
                {t('DOCUMENT_DEMANDER.FILES.DOWNLOAD_ALL')}
            </Button>
            <Button
                variant={ButtonVariant.SECONDARY}
                iconLeft={<LuUpload size={14} />}
                disabled={isDisabledDocumentUpload}
                onClick={() => setShowUploadModal(true)}
            >
                {t('DOCUMENT_DEMANDER.FILES.UPLOAD')}
            </Button>
            <UploadModal
                open={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                disabled={isDisabledDocumentUpload}
                multiple
                uploadFile={uploadFile}
                removeUploaded={removeUploaded}
            />
        </div>
    );
};

export default DocumentActions;
